<template>
  <div
    class="install-app-menu"
    role="region"
    :aria-label="installAppLabel"
  >
    <button
      v-if="type === 'drawer'"
      type="button"
      class="install-app-menu__item"
      :class="{ 'install-app-menu__item--compact': isDrawerCompact }"
      :aria-label="installAppAriaLabel"
      @click="handleInstallClick"
    >
      <template v-if="!isDrawerCompact">
        <span class="install-app-menu__text">
          {{ installAppText }}
        </span>
        <span
          class="install-app-menu__icons"
          aria-hidden="true"
        >
          <atomic-icon id="apple" />
          <atomic-icon id="android" />
        </span>
      </template>

      <template v-else>
        <atomic-icon
          id="install-app"
          aria-hidden="true"
        />
        <span class="visually-hidden">{{ installAppText }}</span>
      </template>
    </button>

    <button
      v-if="type === 'footer'"
      type="button"
      class="install-app-menu__footer"
      :aria-label="installAppAriaLabel"
      @click="handleInstallClick"
    >
      <span class="install-app-menu__footer-text">
        {{ installAppText }}
      </span>
      <span
        class="install-app-menu__footer-icons"
        aria-hidden="true"
      >
        <atomic-icon id="apple" />
        <atomic-icon id="android" />
        <atomic-image
          src="/img/icons/chrome.svg"
          alt="Chrome browser"
          loading="lazy"
        />
        <atomic-image
          src="/img/icons/safari.svg"
          alt="Safari browser"
          loading="lazy"
        />
      </span>
    </button>

    <bottom-sheet-install-app
      v-if="shouldShowBottomSheet"
      ref="bsInstallApp"
    />
  </div>
</template>

<script setup lang="ts">
import type { IBottomSheetComponent } from '@skeleton/core/types';
import { ModalName } from '@skeleton/consts/modals';

interface Props {
  type: 'footer' | 'drawer';
}

defineProps<Props>();

const globalStore = useGlobalStore();
const { layoutData, defaultLocaleLayoutData } = storeToRefs(globalStore);
const { getContent } = useProjectMethods();
const { openModal } = useModalStore();
const layoutStore = useLayoutStore();
const { isDrawerCompact } = storeToRefs(layoutStore);
const { isMobile } = storeToRefs(useDeviceStore());

const { isMounted } = useIsMounted();

const allowInstall = ref(false);
const appleNotCompatible = ref(false);
const isProcessing = ref(false);
const bsInstallApp = ref<IBottomSheetComponent | null>(null);

const installAppText = computed(() =>
  getContent(layoutData.value, defaultLocaleLayoutData.value, 'siteSidebar.installApp') || 'Install App',
);

const installAppLabel = computed(() =>
  'Install mobile app section',
);

const installAppAriaLabel = computed(() =>
  `Install ${installAppText.value} on your device`,
);

const shouldShowBottomSheet = computed(() => {
  return isMounted.value && isMobile.value;
});

const initializePWA = async (): Promise<void> => {
  if (!isMounted.value) return;

  try {
    const { UAParser } = await import('ua-parser-js');

    if (window.navigator?.userAgent) {
      const parsedUserAgent = UAParser(window.navigator.userAgent);
      appleNotCompatible.value
          = parsedUserAgent?.browser?.name === 'Safari'
            || parsedUserAgent?.os?.name === 'iOS';
    }

    if (window.pwa) {
      window.pwa.onInit(() => {
        allowInstall.value = !!window.pwa?.allowInstall;
      });
    }
  }
  catch (error) {
    console.warn('Failed to initialize PWA features:', error);
  }
};

const handleInstallClick = async (): Promise<void> => {
  if (isProcessing.value || !isMounted.value) return;

  isProcessing.value = true;

  try {
    if (allowInstall.value && !appleNotCompatible.value && window.pwa) {
      await window.pwa.install();
      return;
    }

    if (shouldShowBottomSheet.value) {
      await nextTick();

      if (bsInstallApp.value?.open) {
        bsInstallApp.value.open();
        return;
      }
    }
    await openModal(ModalName.INSTALL_APP);
  }
  catch (error) {
    console.warn('Install app action failed:', error);

    try {
      await openModal(ModalName.INSTALL_APP);
    }
    catch (fallbackError) {
      console.error('Fallback modal also failed:', fallbackError);
    }
  }
  finally {
    isProcessing.value = false;
  }
};

onMounted(() => {
  nextTick(() => {
    initializePWA();
  });
});
</script>

<style src="~/assets/styles/components/atomic/menu/install-app.scss" lang="scss" />
